// SAD-186 closeout — pure display/state helpers, split out of CloseoutPage so
// the string-shaping and the saved-state rule can be unit-tested directly
// (matt's round-3 return: per-section SDS bullets, capitalized section names,
// and a truthful saved/pending indicator).
// sdsSectionKeys splits the sds_section column into route keys. The column is
// a single scalar in the seed (one route per item), but nothing prevents it
// from carrying more than one whitespace-joined key, so split defensively.
export function sdsSectionKeys(value) {
    return (value ?? '').split(/\s+/).filter(Boolean);
}
// capitalizeSection turns a route key into a human section name:
// "periods" -> "Periods", "potential-issues" -> "Potential Issues".
export function capitalizeSection(section) {
    return section.split('-').filter(Boolean).map((word)=>word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}
// closeoutSdsBullets is the fix for matt's round-3 item 1: the multiple
// "relevant sections" a checklist item references live as ";"-separated
// clauses inside review_instruction (e.g. item 23 "Periods: … ; Optional
// Tasks: …"), NOT as multiple keys in sds_section. Round 2 bulleted the
// sds_section keys (usually one), so a multi-part review_instruction still
// rendered on a single run-together bullet. Split the instruction on ";" so
// every section reference becomes its own list item. Each clause links to the
// item's section route (they share one route when sds_section is a single
// key); a multi-key sds_section pairs clauses to keys by position. With no
// instruction prose, fall back to one "Review <Section>" bullet per key.
export function closeoutSdsBullets(sdsSection, reviewInstruction) {
    const sections = sdsSectionKeys(sdsSection);
    // No navigable section → no bullets, matching the pre-round-3 guard. A
    // tenant can set review_instruction without an sds_section (the columns are
    // independently nullable); without a section key there is nothing to link
    // to, so don't render section-less bullets that would deep-link to the
    // program root.
    if (sections.length === 0) return [];
    const primary = sections[0];
    const clauses = (reviewInstruction ?? '').split(';').map((clause)=>clause.trim()).filter(Boolean);
    if (clauses.length > 0) {
        const paired = clauses.map((text, i)=>({
                text,
                section: sections[i] ?? primary
            }));
        // Sections beyond the clause count still deserve a navigable bullet, so a
        // multi-key sds_section never silently drops a route when the review
        // instruction describes fewer clauses than there are sections.
        const unpaired = sections.slice(clauses.length).map((section)=>({
                text: `Review ${capitalizeSection(section)}`,
                section
            }));
        return [
            ...paired,
            ...unpaired
        ];
    }
    return sections.map((section)=>({
            text: `Review ${capitalizeSection(section)}`,
            section
        }));
}
// closeoutItemStatus is the fix for matt's round-3 saved-vs-"is saved"
// ambiguity: saved and unsaved rows looked identical, so an item edited but
// not Saved read as done on screen while the completion gate (which only sees
// persisted rows) treated it as incomplete. This mirrors the gate's real rule
// (delivery_closeout_repository.go Complete): an item counts only when its
// PERSISTED answer is set, and — for N/A or No — carries a persisted comment.
// Pending unsaved edits are called out separately so the user knows a Save is
// owed before Complete will accept the row.
export function closeoutItemStatus(persisted, draft) {
    const dirty = draft.answer !== persisted.answer || draft.comments !== persisted.comments;
    if (dirty) return 'unsaved';
    if (!persisted.answer) return 'unanswered';
    if (persisted.answer !== 'YES' && persisted.comments.trim() === '') return 'needs-comment';
    return 'saved';
}
// responsiblePartyOptions resolves matt's round-3 ask that Responsible Party be
// a dropdown of the program's Roles users including the current user, rather
// than free text. It reuses the program role-assignment roster (the same rows
// the Roles surface renders) and unions the signed-in user, deduped by display
// name (case-insensitive) and sorted so the dropdown is stable. The stored
// value stays the human name string — the export reads it verbatim — so no
// schema change is needed.
export function responsiblePartyOptions(rosterRows, currentUser) {
    const out = [];
    const seen = new Set();
    const add = (rawName, rawEmail)=>{
        const name = (rawName || rawEmail || '').trim();
        if (!name) return;
        const key = name.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        out.push({
            name,
            email: (rawEmail || '').trim()
        });
    };
    for (const row of rosterRows)add(row.contactDisplayName, row.contactEmail);
    if (currentUser) add(currentUser.name, currentUser.email);
    return out.sort((a, b)=>a.name.localeCompare(b.name));
}
